(() => {
  function initHero(root) {
    if (root.dataset.rackReady) return;
    root.dataset.rackReady = 'true';
    const track = root.querySelector('[data-slide-track]');
    const slides = [...root.querySelectorAll('[data-slide]')];
    const dots = [...root.querySelectorAll('[data-dot]')];
    if (!track || !slides.length) return;
    let current = 0;
    let transitioning = false;
    let wrapPosition = null;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    function updateState() {
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === current);
        slide.setAttribute('aria-hidden', i === current ? 'false' : 'true');
        slide.inert = i !== current;
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === current);
        dot.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
    }
    function position(index, animate) {
      if (!animate) track.style.transition = 'none';
      track.style.transform = `translate3d(-${index * 100}%,0,0)`;
      if (!animate) {
        track.offsetHeight;
        track.style.transition = '';
      }
    }
    if (slides.length > 1) {
      const firstClone = slides[0].cloneNode(true);
      const lastClone = slides[slides.length - 1].cloneNode(true);
      for (const clone of [firstClone, lastClone]) {
        clone.removeAttribute('data-slide');
        clone.removeAttribute('data-shopify-editor-block');
        clone.classList.remove('is-active');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
      }
      track.prepend(lastClone);
      track.append(firstClone);
      position(1, false);
    }
    updateState();
    function show(index) {
      if (slides.length < 2 || transitioning) return;
      if (index === current) return;
      if (reducedMotion.matches) {
        current = (index + slides.length) % slides.length;
        position(current + 1, false);
        updateState();
        return;
      }
      transitioning = true;
      if (index >= slides.length) {
        current = 0;
        wrapPosition = 1;
        position(slides.length + 1, true);
      } else if (index < 0) {
        current = slides.length - 1;
        wrapPosition = slides.length;
        position(0, true);
      } else {
        current = index;
        wrapPosition = null;
        position(current + 1, true);
      }
      updateState();
    }
    track.addEventListener('transitionend', event => {
      if (event.target !== track || event.propertyName !== 'transform') return;
      if (wrapPosition !== null) position(wrapPosition, false);
      wrapPosition = null;
      transitioning = false;
    });
    root.querySelector('[data-prev]')?.addEventListener('click', () => show(current - 1));
    root.querySelector('[data-next]')?.addEventListener('click', () => show(current + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
    root.addEventListener('shopify:block:select', event => {
      const index = slides.findIndex(slide => slide === event.target || slide.contains(event.target));
      if (index < 0) return;
      transitioning = false;
      wrapPosition = null;
      current = index;
      position(index + 1, false);
      updateState();
    });
    if (slides.length > 1 && !reducedMotion.matches) {
      const timer = window.setInterval(() => {
        if (!root.isConnected) return window.clearInterval(timer);
        if (document.hidden) return;
        show(current + 1);
      }, 5000);
    }
  }
  function initReviews(root) {
    if (root.dataset.rackReady) return;
    root.dataset.rackReady = 'true';
    const track = root.querySelector('[data-review-track]');
    const cards = [...(track?.querySelectorAll('.rack-reviews__card') || [])];
    if (!track || cards.length < 2) return;
    const clones = cards.map(card => {
      const clone = card.cloneNode(true);
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      return clone;
    });
    track.append(...clones);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const { signal } = controller;
    let stepWidth = 0;
    let cycleWidth = 0;
    let canScroll = false;
    let inView = false;
    let hovered = false;
    let frame = 0;
    let lastFrame = 0;
    let position = 0;
    function stop() {
      window.cancelAnimationFrame(frame);
      frame = 0;
      lastFrame = 0;
    }
    function animate(time) {
      if (!root.isConnected) return cleanup();
      if (lastFrame) {
        position = (position + Math.min(time - lastFrame, 64) * 0.045) % cycleWidth;
        track.scrollLeft = position;
      }
      lastFrame = time;
      frame = window.requestAnimationFrame(animate);
    }
    function updateMotion() {
      const shouldRun = inView && canScroll && !document.hidden && !reducedMotion.matches && !hovered;
      if (!shouldRun) return stop();
      if (frame) return;
      position = track.scrollLeft % cycleWidth;
      frame = window.requestAnimationFrame(animate);
    }
    function measure() {
      stop();
      stepWidth = cards[0].getBoundingClientRect().width + 20;
      cycleWidth = clones[0].offsetLeft - cards[0].offsetLeft;
      canScroll = cycleWidth > 0 && track.scrollWidth > track.clientWidth + 2;
      updateMotion();
    }
    function scroll(direction) {
      if (!canScroll) return;
      if (frame) {
        position = (track.scrollLeft + direction * stepWidth + cycleWidth) % cycleWidth;
        track.scrollLeft = position;
        return;
      }
      if (track.scrollLeft >= cycleWidth) track.scrollLeft %= cycleWidth;
      if (direction < 0 && track.scrollLeft < stepWidth) track.scrollLeft += cycleWidth;
      track.scrollBy({ left: direction * stepWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }
    root.querySelector('[data-review-prev]')?.addEventListener('click', () => scroll(-1), { signal });
    root.querySelector('[data-review-next]')?.addEventListener('click', () => scroll(1), { signal });
    root.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      updateMotion();
    }, { signal });
    root.addEventListener('pointerleave', () => { hovered = false; updateMotion(); }, { signal });
    track.addEventListener('scroll', () => {
      const actualPosition = track.scrollLeft;
      if (cycleWidth && Math.abs(actualPosition - position) > 2) position = actualPosition % cycleWidth;
    }, { passive: true, signal });
    document.addEventListener('visibilitychange', updateMotion, { signal });
    reducedMotion.addEventListener('change', updateMotion, { signal });
    const visibility = new IntersectionObserver(entries => {
      inView = entries[0].isIntersecting;
      updateMotion();
    });
    const sizes = new ResizeObserver(measure);
    function cleanup() {
      stop();
      visibility.disconnect();
      sizes.disconnect();
      controller.abort();
    }
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(root)) cleanup();
    }, { signal });
    visibility.observe(root);
    sizes.observe(track);
    measure();
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-rack-slider]').forEach(initHero);
    scope.querySelectorAll('[data-rack-reviews]').forEach(initReviews);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
